import store from 'store/dist/store.modern';
import observePlugin from 'store/plugins/observe';
import differenceBy from 'lodash-es/differenceBy';
import getTarget from '../utils/get-target';
import { API_URL } from '../constants/environment.dev';

// Types
export const TYPES = {
  OBJECT: 1,
};

export const TYPES_STRINGS = {
  1: 'object',
};

// Bookmark store
export class BookmarkStore {
  constructor(bookmarkStore) {
    this.store = bookmarkStore;
    this.bookmarks = this.store.get('bookmarks') || [];
    this.updateEvents = { add: [], remove: [], update: [] };

    // Observe event
    this.store.observe('bookmarks', (b, oB) => this.diff(b, oB));
    this.checkItems();

    // Check for updates
    window.addEventListener('storage', (event) => {
      if (event.key === 'bookmarksLastUpdate') {
        const oldBookmarks = this.bookmarks.slice(0);
        this.bookmarks = this.store.get('bookmarks');
        this.diff(this.bookmarks, oldBookmarks);
      }
    }, false);
  }

  diff(bookmarks, oldBookmarks) {
    // Don't run on init
    if (oldBookmarks === undefined) return;

    // Get new items
    const generateIdForItem = b => `type:${b.type}:id:${b.id}`;
    const addedItems = differenceBy(bookmarks, oldBookmarks, generateIdForItem);
    const removedItems = differenceBy(oldBookmarks, bookmarks, generateIdForItem);

    // Send update events
    this.updateEvents.add.forEach(cb => cb.call(null, 'add', addedItems));
    this.updateEvents.remove.forEach(cb => cb.call(null, 'remove', removedItems));
    this.updateEvents.update.forEach(cb => cb.call(null, 'update'));
  }

  addUpdateEvent(types, callback) {
    types.forEach(type => this.updateEvents[type].push(callback));
  }

  save() {
    this.store.set('bookmarks', this.bookmarks);
    this.store.set('bookmarksLastUpdate', Date.now());
  }

  addItem(
    id,
    type = TYPES.OBJECT,
  ) {
    // Don't save again
    if (this.isBookmarked(id, type)) return;

    // Add item to store
    const item = { id, type };
    this.bookmarks.push(item);

    // Save store
    this.save();
  }

  removeItem(id, type = TYPES.OBJECT) {
    // Remove item from store
    const item = this.getItem(id, type);
    this.bookmarks = this.bookmarks.filter(b => b !== item);

    // Save store
    this.save();
  }

  checkItems() {
    const $this = this;
    this.bookmarks.forEach((bookmark) => {
      const xhttp = new XMLHttpRequest();
      xhttp.onreadystatechange = function () {
        if (this.readyState === 4 && this.status === 404) {
          $this.removeItem(bookmark.id, bookmark.type);
        }
      };
      xhttp.open('GET', `${API_URL}/${encodeURIComponent(bookmark.id)}`, true);
      xhttp.send();
    });
  }

  getItem(id, type = TYPES.OBJECT) {
    return this.bookmarks.filter(b => b.id === id && b.type === type).reduce((_, b) => b);
  }

  getItems() {
    return this.bookmarks;
  }

  getItemsByType(type = TYPES.OBJECT) {
    return this.bookmarks.filter(b => b.type === type);
  }

  isBookmarked(id, type = TYPES.OBJECT) {
    return this.bookmarks.some(b => b.id === id && b.type === type);
  }

  count() {
    return this.bookmarks.length;
  }

  countByType(type = TYPES.OBJECT) {
    return this.getItemsByType(type).length;
  }
}

// Apply plugins
store.addPlugin(observePlugin);

// Create bookmark store
const bookmarkStore = new BookmarkStore(store);

// Parse type string/id
export function getTypeIdFromString(input) {
  // Translate string to type ID
  if (/^\+?(0|[1-9]\d*)$/.test(input)) {
    return parseInt(input, 10);
  }

  return TYPES[input.toUpperCase()];
}

// Parse data-* from element
function getItemForElement($el) {
  // Get id and type
  try {
    const id = $el.getAttribute('data-id');
    const type = getTypeIdFromString($el.getAttribute('data-type'));

    // Return item object
    return {
      id,
      type,
    };
  } catch (e) {
    return false;
  }
}

// Add/remove bookmark button
document.addEventListener('click', (event) => {
  const $target = getTarget(event.target, '.js-bookmark');

  // Check if item was clicked
  if ($target) {
    // Get item for element
    const item = getItemForElement($target);

    // Item not found
    if (item === false) {
      return;
    }

    // Save bookmark
    if (!bookmarkStore.isBookmarked(item.id, item.type)) {
      bookmarkStore.addItem(
        item.id,
        item.type,
      );
      $target.classList.add('js-is-bookmarked');
    } else {
      bookmarkStore.removeItem(item.id, item.type);
      $target.classList.remove('js-is-bookmarked');
    }
  }
});

// Add .js-is-bookmarked to all bookmark buttons
document.querySelectorAll('.js-bookmark').forEach(($el) => {
  // Get item for element
  const item = getItemForElement($el);

  // Item not found
  if (item === false) {
    return;
  }

  const $label = $el.querySelector('.expose-stage__function-label');
  const { labelBookmarked } = $el.dataset;

  // Has bookmark?
  if (bookmarkStore.isBookmarked(item.id, item.type)) {
    $el.classList.add('js-is-bookmarked');
    $label.innerHTML = labelBookmarked;
  }

  // Disable focus catch by click
  $el.addEventListener('mousedown', e => e.preventDefault());
});

// Show/hide on update events
bookmarkStore.addUpdateEvent(['add', 'remove'], (event, items) => {
  items.forEach((item) => {
    const typeString = TYPES_STRINGS[item.type];

    document.querySelectorAll(`.js-bookmark[data-id="${item.id}"][data-type="${typeString}"]`).forEach(($el) => {
      const { labelBookmarked } = $el.dataset;
      const { labelBookmark } = $el.dataset;
      const $label = $el.querySelector('.expose-stage__function-label');

      if (event === 'add') {
        $el.classList.add('js-is-bookmarked');
        $label.innerHTML = labelBookmarked;
      } else {
        $el.classList.remove('js-is-bookmarked');
        $label.innerHTML = labelBookmark;
      }
    });
  });
});

// Counter
document.querySelectorAll('.js-bookmark-count').forEach(($el) => {
  // Update function
  const update = () => {
    const count = bookmarkStore.count();
    // eslint-disable-next-line no-param-reassign
    $el.querySelector('.header__button-count').innerHTML = count;

    // Show/hide bookmark counter bubble
    if (count > 0) {
      $el.classList.remove('u-hidden');
    } else {
      $el.classList.add('u-hidden');
    }
  };

  // Add and run update event
  bookmarkStore.addUpdateEvent(['update'], update);
  update();
});

export default bookmarkStore;
